import { useMediaQuery } from '@mui/material';
import {
  blue,
  green,
  lightBlue,
  purple
} from '@mui/material/colors';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useParams } from 'react-router-dom';
import Expand from 'assets/images/icons/expand.png';
import { brandColors } from 'types/colors';
export const LabelType = {
  String: 'category',
  Date: 'auto',
  Number: 'numeric',
};


const customYAxisLimits = {
  temperature: { min: 12, max: 50 },
  temperature_avg: { min: 12, max: 50 },
  set_point: { min: 15, max: 35 },
  clean_air_level: { min: 50, max: 100 },
  ventilation: { min: 50, max: 100 },
  comfort_level: { min: 50, max: 100 },
  rh: { min: 30, max: 100 },
  cool_water: { min: 0, max: 100 },
};

const ONE_DAY = 86400;
const MOTION_COUNTER_MAX = 300;
const DEFAULT_GRADIENT_COLORS = [lightBlue[200], blue[400]];
const DECIMAL_POINTS = 2;
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_COLORS = [blue[400], green[400], purple[400]];
const chartRangePillStyle = `
  display:inline-flex;
  align-items:center;
  justify-content:center;
  height:20px;
  padding:0 8px;
  border:1px solid #90a4ae;
  border-radius:999px;
  background:#fff;
  color:${brandColors.BLUE};
  font-size:11px;
  font-weight:600;
  line-height:20px;
  white-space:nowrap;
`;
const activeChartRangePillStyle = `
  background:${brandColors.BLUE};
  border-color:${brandColors.BLUE};
  color:#fff;
`;
export function makeSeriesTooltipFormatter(nameFormatters) {
  // nameFormatters: { [seriesName: string]: (value, ctx) => string }
  return (value, ctx) => {
    const { seriesIndex, w } = ctx;
    const seriesName = w?.config?.series?.[seriesIndex]?.name;
    const fmt = seriesName && nameFormatters[seriesName];
    return fmt ? fmt(value, ctx) : String(value);
  };
}

const findNearestIndex = (xValues, targetX) => {
  if (!Array.isArray(xValues) || xValues.length === 0 || targetX === undefined) {
    return -1;
  }

  let closestIndex = -1;
  let closestDelta = Infinity;
  xValues.forEach((x, idx) => {
    if (!Number.isFinite(x)) return;
    const delta = Math.abs(x - targetX);
    if (delta < closestDelta) {
      closestDelta = delta;
      closestIndex = idx;
    }
  });
  return closestIndex;
};

const addValidIndex = (indices, value) => {
  const index = Number(value);

  if (Number.isInteger(index) && index >= 0) {
    indices.add(index);
  }
};

const getHiddenSeriesIndices = (w, rootElement) => {
  const hidden = new Set();

  (w?.globals?.collapsedSeriesIndices ?? []).forEach(index => addValidIndex(hidden, index));
  (w?.globals?.ancillaryCollapsedSeriesIndices ?? []).forEach(index => addValidIndex(hidden, index));
  (w?.globals?.collapsedSeries ?? []).forEach(series => addValidIndex(hidden, series?.index));
  (w?.globals?.ancillaryCollapsedSeries ?? []).forEach(series => addValidIndex(hidden, series?.index));

  const legendRoot = w?.globals?.dom?.elLegendWrap ?? rootElement;
  const legendItems = legendRoot?.querySelectorAll?.('.apexcharts-legend-marker, .apexcharts-legend-text') ?? [];
  legendItems.forEach(element => {
    const isCollapsed =
      element.getAttribute('data:collapsed') === 'true' || element.classList.contains('apexcharts-inactive-legend');

    if (!isCollapsed) {
      return;
    }

    const zeroBasedIndex = element.getAttribute('i');
    const oneBasedIndex = element.getAttribute('rel');

    if (zeroBasedIndex !== null) {
      addValidIndex(hidden, zeroBasedIndex);
    } else if (oneBasedIndex !== null) {
      addValidIndex(hidden, Number(oneBasedIndex) - 1);
    }
  });

  return hidden;
};

const getTranslateY = transformValue => {
  const translateMatch = /translate\([^,\s]+[,\s]+([^)]+)\)/.exec(transformValue ?? '');

  return translateMatch ? Number.parseFloat(translateMatch[1]) : 0;
};

/**
 * Creates a line chart based on react js.
 * If the datasets' data is made of objects, the value (number) itself should be under
 * a property named 'value'
 * @param {*} props.isZoomEnabled
 * @param {*} props.datasets
 * @param {*} props.isLineCurved
 * @param {*} props.isHidden
 * @param {*} props.xAxis.labelType
 * @param {*} props.xAxis.show
 * @param {*} props.fill
 * @param {*} props.strokeWidth
 * @param {*} props.colors
 * @param {*} props.detailed // wether you see stuff like lines and axis labels
 * @returns
 */
export const LineChart = props => {
  const deviceId = useParams().id;
  const chartRef = useRef(deviceId);
  const rootRef = useRef(null);
  const chartBodyRef = useRef(null);
  const [compactPlotBounds, setCompactPlotBounds] = useState({ top: 0, height: 0 });
  const rangeToolbarIcons = (props.chartRangeButtons ?? []).map(button => ({
    title: button.label,
    class: `solin-chart-range-button${button.isActive ? ' solin-chart-range-button-active' : ''}`,
    icon: `<span style="${chartRangePillStyle}${button.isActive ? activeChartRangePillStyle : ''}">${button.label}</span>`,
    click: () => props.onChartRangeSelect?.(button),
  }));
  const expandToolbarIcons = props.onExpand
    ? [
        {
          title: 'Full Screen',
          class: 'solin-chart-expand-button',
          icon: `<img alt="" height="12" src=${Expand} />`,
          click: props.onExpand,
        },
      ]
    : [];
  const defaultSeriesTooltipFormatter = makeSeriesTooltipFormatter({
    "ON/OFF": (v) => (v === 1 ? "ON" : "OFF"),
    "Channel 1": (v) => (v === 1 ? "ON" : "OFF"),
    "Channel 2": (v) => (v === 1 ? "ON" : "OFF"),
    "HVAC Power": (v) => (v === 1 ? "ON" : "OFF"),
    "Temperature": (v) => `${v}°C`,
    "Set Point": (v) => `${v}°C`,
    "PM 2.5": (v) => `${Number(v).toFixed(1)} (µg/m³)`,
    "PM 10": (v) => `${Number(v).toFixed(1)} (µg/m³)`,
    "Occupancy": (v) => `${Math.round(v)}%`,
    "Motion": (v) => `${Math.round(v)}%`,
    "RH": (v) => `${Math.round(v)}%`,
    "TVOC": (v) => `${Math.round(v)} (µg/m³)`,
    "Differential Pressure": (v) => `${Number(v).toFixed(3)} Pa`,
    "Co2": (v) => `${Math.round(v)} ppm`,
    "Fan Speed": (v) => v === 0 ? 'Auto' : v === 1 ? 'Low' : v === 2 ? 'Medium' : v === 3 ? 'High' : 'N/A',
    "Control": (v) => v === 0 ? 'DCV OFF' : v === 1 ? 'SOL AI' : v === 2 ? 'Manual' : v === 3 ? 'Schedule' : 'N/A',
    "Cool Water": (v) => `${v}%`,
    "Ventilation": (v) => `${v}%`,
    "Clean Air Level": (v) => `${v}%`,
    "Comfort Level": (v) => `${v}%`,
  });
  const defaultValueFormatter = (value, ctx) => {
    const measurementName = ctx?.w?.config?.series?.[ctx.seriesIndex]?.measurementName;

    if (/(?:_saveable|_justified|_requested|_served)$/.test(measurementName ?? '')) {
      return `${value}%`;
    }

    return defaultSeriesTooltipFormatter(value, ctx);
  };
  const tooltipValueFormatter = props.formatTooltipValue ?? defaultValueFormatter;
  const mapMotionCounterToPercent = value => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return value;
    }

    return (Number(value) / MOTION_COUNTER_MAX) * 100;
  };
  const normalizeDataset = dataset => {
    if (!dataset || dataset.label !== 'Motion' || !Array.isArray(dataset.data)) {
      return dataset;
    }

    return {
      ...dataset,
      data: dataset.data.map(point => {
        if (point && typeof point === 'object') {
          return {
            ...point,
            value: mapMotionCounterToPercent(point.value),
          };
        }

        return mapMotionCounterToPercent(point);
      }),
    };
  };
  const normalizedDatasets = props.datasets?.map(normalizeDataset);
  const renderDataLabelIcons = () => {
    if (!props.dataLabelIcon || !rootRef.current) return;

    rootRef.current.querySelectorAll('[data-solin-data-label-icon]').forEach(icon => icon.remove());
    rootRef.current.querySelectorAll('.apexcharts-datalabel').forEach(label => {
      const lines = label.textContent?.split('\n');
      if (!lines || lines.length < 2 || label.getAttribute('data-solin-multiline-label')) return;

      const x = label.getAttribute('x');
      label.textContent = '';
      lines.forEach((line, index) => {
        const lineElement = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        lineElement.setAttribute('x', x ?? '0');
        lineElement.setAttribute('dy', index === 0 ? '-0.6em' : '1.2em');
        lineElement.textContent = line;
        label.appendChild(lineElement);
      });
      label.setAttribute('data-solin-multiline-label', 'true');
    });
    rootRef.current.querySelectorAll('.apexcharts-datalabels').forEach((labels, seriesIndex) => {
      if (props.dataLabelIconSeries && !props.dataLabelIconSeries[seriesIndex]) return;

      labels.querySelectorAll('.apexcharts-datalabel').forEach(label => {
        const labelBounds = label.getBBox();
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'image');

        icon.setAttribute('data-solin-data-label-icon', 'true');
        icon.setAttribute('href', props.dataLabelIcon);
        icon.setAttribute('x', `${labelBounds.x + labelBounds.width - 5}`);
        icon.setAttribute('y', `${labelBounds.y + labelBounds.height - 22}`);
        icon.setAttribute('width', '28');
        icon.setAttribute('height', '28');
        icon.setAttribute('pointer-events', 'none');
        label.parentNode?.appendChild(icon);
      });
    });
  };
  const getData = (values, label) => {
    let now = Math.floor(Date.now() / 1000);

    if (values && isNaN(values[0])) {
      return values.map(obj => {
        return {
          ...obj,
          // x: DateTime.fromSeconds(obj.unix_time).toFormat('dd.MM HH:mm'),
          x: obj.unix_time * 1000,
          y: label === 'Fan Speed' ? String(obj.value).toLowerCase() === 'auto' ? 0 : String(obj.value).toLowerCase() === 'low' ? 1 : String(obj.value).toLowerCase() === 'medium' ? 2 : String(obj.value).toLowerCase() === 'high' ? 3 : obj.value : obj.value,
        };
      });
    }

    return values;
  };

  const chartColors = props.colors ?? DEFAULT_COLORS;
  const compactAxes = props.compactAxes === true;

  let series = normalizedDatasets?.map(value => {
    if (value) {
      return {
        name: value.label.replace(/(?:_|\s)avg$/i, ''),
        measurementName: value.measurementName,
        data: getData(value.data, value.label),
      };
    }
  });

  const title = typeof props.title === 'string' ? props.title : null;
  const isXsScreen = useMediaQuery(theme => theme.breakpoints.only('xs'));

  const getPointValue = point => Number(point && typeof point === 'object' ? point.value : point);

  const getYAxisConfig = (seriesObj, seriesIndex) => {
    const yAxisRange = props.yAxisRanges?.[seriesIndex] ?? props.yAxisRange;
    const isOnOff = seriesObj.label === 'ON/OFF' || seriesObj.label === 'HVAC Power';
    const isChannel1 = seriesObj.label === 'Channel 1';
    const isChannel2 = seriesObj.label === 'Channel 2';
    const isFanSpeed = seriesObj.label === 'Fan Speed';
    const isControl = seriesObj.label === 'Control';
    const isOccupancy = seriesObj.label === 'Occupancy';
    const isMotion = seriesObj.label === 'Motion';
    const isKwh = seriesObj.label === 'KWH';
    const isPM25 = seriesObj.label === 'PM 2.5';
    const key = seriesObj.label.toLowerCase().replace(/ /g, "_");
    const isDemandPercentage = /_(served|requested|justified|saveable)$/.test(key);
    const numericValues = (seriesObj.data ?? []).map(getPointValue).filter(Number.isFinite);
    const minAuto = numericValues.length ? Math.min(...numericValues) : 0;
    const maxAuto = numericValues.length ? Math.max(...numericValues) : 1;
    const axisColor = chartColors[seriesIndex % chartColors.length];
    const tickAmount = compactAxes ? 4 : isControl ? 3 : isOnOff ? 1 : isFanSpeed ? 3 : 10;
    const formatter = val => {
      if (isOnOff || isChannel1 || isChannel2) {
        if (val === 0) return 'OFF';
        if (val === 1) return 'ON';
        return '';
      } else if (isFanSpeed) {
        if (val === 0) {
          return 'AUTO';
        } else if (val === 1) {
          return 'LOW';
        } else if (val === 2) {
          return 'MEDIUM';
        } else if (val === 3) {
          return 'HIGH';
        }
      } else if (isControl) {
        if (val === 0) {
          return 'DCV OFF';
        } else if (val === 1) {
          return 'SOL AI';
        } else if (val === 2) {
          return 'Manual';
        } else if (val === 3) {
          return 'Schedule';
        }
      } else if (isKwh) {
        return Number(val).toFixed(2);
      } else if (isPM25) {
        return Number(val).toFixed(1);
      } else if (isMotion) {
        return `${Math.round(Number(val))}%`;
      }
      return Math.round(Number(val));
    };

    return {
      color: axisColor,
      formatter,
      label: seriesObj.label.replace(/(?:_|\s)avg$/i, ''),
      min: isDemandPercentage ? 0 : yAxisRange?.min ?? (isControl ? 0 : isOnOff || isFanSpeed || isOccupancy || isMotion ? 0 : (customYAxisLimits[key]?.min ?? minAuto)),
      max: isDemandPercentage ? 100 : yAxisRange?.max ?? (isControl ? 3 : isOnOff ? 1 : isFanSpeed ? 3 : isMotion ? 100 : isOccupancy && props.occupancyAxisMinimumMax != null ? Math.max(props.occupancyAxisMinimumMax, maxAuto) : (customYAxisLimits[key]?.max ?? maxAuto)),
      tickAmount,
      decimalsInFloat: isKwh ? 2 : isPM25 ? 1 : 0,
    };
  };

  const axisConfigs = normalizedDatasets?.map(getYAxisConfig).filter(Boolean) ?? [];
  const largestSeriesLength = Math.max(0, ...series.map(seriesItem => seriesItem?.data?.length ?? 0));
  const showPointValues = largestSeriesLength <= 40;
  const getCompactAxisTicks = axis => {
    const min = Number.isFinite(axis.min) ? axis.min : 0;
    const max = Number.isFinite(axis.max) ? axis.max : min + 1;
    const tickAmount = Math.max(axis.tickAmount, 1);

    return Array.from({ length: tickAmount + 1 }, (_, index) => max - ((max - min) / tickAmount) * index);
  };

  const getCompactTickTop = (axis, tick) => {
    const min = Number.isFinite(axis.min) ? axis.min : 0;
    const max = Number.isFinite(axis.max) ? axis.max : min + 1;

    if (max === min) {
      return '50%';
    }

    return `${((max - tick) / (max - min)) * 100}%`;
  };

  useEffect(() => {
    if (!compactAxes) {
      return undefined;
    }

    let frameId;
    const updateCompactPlotBounds = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const svg = chartBodyRef.current?.querySelector('.apexcharts-svg');
        const inner = chartBodyRef.current?.querySelector('.apexcharts-inner');
        const grid = chartBodyRef.current?.querySelector('.apexcharts-grid');
        const gridBox = typeof grid?.getBBox === 'function' ? grid.getBBox() : null;

        if (!svg || !gridBox || gridBox.height <= 0) {
          return;
        }

        const chartBodyOffsetTop = chartBodyRef.current?.offsetTop ?? 0;
        const svgOffsetTop = svg.offsetTop ?? 0;
        const gridOffsetTop = getTranslateY(inner?.getAttribute('transform')) + gridBox.y;
        const nextBounds = {
          top: chartBodyOffsetTop + svgOffsetTop + gridOffsetTop,
          height: gridBox.height,
        };

        setCompactPlotBounds(currentBounds =>
          Math.abs(currentBounds.top - nextBounds.top) < 0.5 && Math.abs(currentBounds.height - nextBounds.height) < 0.5
            ? currentBounds
            : nextBounds,
        );
      });
    };

    updateCompactPlotBounds();
    const fallbackTimerId = window.setTimeout(updateCompactPlotBounds, 250);

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateCompactPlotBounds) : null;

    if (resizeObserver) {
      if (rootRef.current) {
        resizeObserver.observe(rootRef.current);
      }
      if (chartBodyRef.current) {
        resizeObserver.observe(chartBodyRef.current);
      }
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(fallbackTimerId);
      resizeObserver?.disconnect();
    };
  }, [compactAxes, props.datasets, props.height, props.width]);

  // const leftSideYAxis = useSelector(state => state.controllers.selectedGraphs)

  const options = () => {
    return {
      datasets: normalizedDatasets,
      chart: {
        id: deviceId,
        type: props.fill ? 'area' : 'line',
        parentHeightOffset: compactAxes ? 0 : undefined,
        accessibility: {
          enabled: false,
        },
        stacked: false,
        zoom: {
          type: 'x',
          enabled: true,
          autoScaleYaxis: !props.yAxisRange && !props.yAxisRanges,
        },
        events: {
          mounted: chartContext => {
            // ApexCharts otherwise uses both x and y distance when series have
            // different timestamps, which makes a shared tooltip require hovering
            // near a line. Our custom tooltip resolves each series by nearest time.
            chartContext.w.globals.allSeriesHasEqualX = true;
            window.requestAnimationFrame(renderDataLabelIcons);
          },
          updated: chartContext => {
            chartContext.w.globals.allSeriesHasEqualX = true;
            window.requestAnimationFrame(renderDataLabelIcons);
          },
          legendClick: (chartContext, seriesIndex) => {
            if (!props.preventAllSeriesHidden) {
              return undefined;
            }

            const hiddenSeries = getHiddenSeriesIndices(chartContext.w, rootRef.current);
            const visibleSeriesCount = (chartContext.w.globals.seriesNames ?? []).length - hiddenSeries.size;

            // The Sensor Page controls its legend toggle so the final visible
            // series cannot be hidden.
            if (visibleSeriesCount === 1 && !hiddenSeries.has(seriesIndex)) {
              return undefined;
            }

            chartContext.toggleSeries(chartContext.w.globals.seriesNames[seriesIndex]);
            return undefined;
          },
        },
        toolbar: {
          export: {
            csv: {
              filename: title + ' ' + new Date().toLocaleDateString(),
              headerCategory: `${props.sensorId}: Date`,
              columnDelimiter: ',',
              dateFormatter: function (timestamp) {
                return new Date(timestamp)
                  .toLocaleDateString('en-En', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'UTC',
                  })
                  .replace(/,/, '');
              },
            },
          },
          autoSelected: 'zoom',
          show: props.toolbar ? props.toolbar.show : true,
          offsetX: props.toolbar ? props.toolbar.offsetX : 0,
          offsetY: props.toolbar ? props.toolbar.offsetY : 0,
          tools: {
            download: props.detailed ?? true,
            selection: props.detailed ?? true,
            zoom: props.detailed ?? true,
            zoomin: props.detailed ?? true,
            zoomout: props.detailed ?? true,
            pan: props.detailed ?? true,
            reset: props.detailed ?? true,
            customIcons: [...rangeToolbarIcons, ...expandToolbarIcons],
          },
        },
      },
      legend: {
        inverseOrder: true, // Reverses the order of the legend items
        onItemClick: {
          toggleDataSeries: !props.preventAllSeriesHidden,
        },
      },
      dataLabels: {
        enabled: showPointValues,
        background: {
          enabled: false,
        },
        offsetY: -8,
        style: {
          colors: chartColors,
          fontSize: '12px',
          fontWeight: 500,
        },
        formatter: (value, { seriesIndex, dataPointIndex, w }) => props.formatDataLabelValue
          ? props.formatDataLabelValue(value, { seriesIndex, dataPointIndex, w })
          : tooltipValueFormatter(value, { seriesIndex, w }),
      },
      markers: {
        size: showPointValues ? 4 : 0,
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const collapsed = getHiddenSeriesIndices(w, rootRef.current);
          const visibleSeries = (w?.globals?.seriesNames ?? []).map((_, idx) => idx)
            .filter(idx => !collapsed.has(idx));

          if (visibleSeries.length === 0) return '';

          const resolvedIndex = Number.isInteger(dataPointIndex)
            ? dataPointIndex
            : (w?.globals?.capturedDataPointIndex ?? -1);
          const preferredSeriesIndex = Number.isInteger(seriesIndex)
            ? seriesIndex
            : visibleSeries[0];

          let hoverX;
          const preferredSeriesX = w?.globals?.seriesX?.[preferredSeriesIndex];
          if (Array.isArray(preferredSeriesX) && resolvedIndex >= 0) {
            hoverX = preferredSeriesX[resolvedIndex];
          }
          if (hoverX === undefined && resolvedIndex >= 0) {
            for (const idx of visibleSeries) {
              const seriesX = w?.globals?.seriesX?.[idx];
              if (Array.isArray(seriesX) && typeof seriesX[resolvedIndex] !== 'undefined') {
                hoverX = seriesX[resolvedIndex];
                break;
              }
            }
          }

          const title = Number.isFinite(hoverX)
            ? DateTime.fromMillis(hoverX).toFormat('HH:mm, dd/M')
            : '';

          const groups = visibleSeries.map(idx => {
            const seriesX = w?.globals?.seriesX?.[idx] ?? [];
            const seriesValues = w?.globals?.series?.[idx] ?? [];
            const valueIndex = Number.isFinite(hoverX)
              ? findNearestIndex(seriesX, hoverX)
              : (resolvedIndex >= 0 && resolvedIndex < seriesValues.length ? resolvedIndex : -1);
            const rawValue = valueIndex >= 0 ? seriesValues[valueIndex] : undefined;
            const formatted = rawValue === null || rawValue === undefined || Number.isNaN(rawValue)
              ? 'N/A'
              : tooltipValueFormatter(rawValue, { seriesIndex: idx, dataPointIndex: valueIndex, w });
            const color = w?.globals?.colors?.[idx] ?? '#000';
            const name = w?.globals?.seriesNames?.[idx] ?? '';
            return `
              <div class="apexcharts-tooltip-series-group apexcharts-active" style="display:flex; align-items:center;">
                <span class="apexcharts-tooltip-marker" style="background-color:${color}"></span>
                <div class="apexcharts-tooltip-text">
                  <div class="apexcharts-tooltip-y-group">
                    <span class="apexcharts-tooltip-text-y-label">${name}: </span>
                    <span class="apexcharts-tooltip-text-y-value">${formatted}</span>
                  </div>
                </div>
              </div>
            `;
          }).join('');

          return `
            ${title ? `<div class="apexcharts-tooltip-title">${title}</div>` : ''}
            ${groups}
          `;
        },
        x: {
          show: true,
          // format: getFormatByLabelType(props.xAxis.labelType),
          formatter: (val) => {
            return DateTime.fromMillis(val).toFormat('HH:mm, dd/M');
          }
        },
        y: {
          decimalsInFloat: DECIMAL_POINTS,
          formatter: tooltipValueFormatter,
        },
      },
      colors: chartColors,
      stroke: {
        show: !props.fill,
        width: props.strokeWidth ?? DEFAULT_STROKE_WIDTH,
        // curve: props.isLineCurved ? 'smooth' : 'straight',
        curve: 'smooth',
        colors: chartColors,
      },
      xaxis: {
        tickAmount: 15,
        reversed: false,
        type: props.xAxis.labelType ?? LabelType.String,
        labels: {
          show: props.xAxis?.show ?? true,
          // offsetX: -10,
          offsetY: 6,
          // format: getFormatByLabelType(props.xAxis.labelType),
          formatter: (val) => {
            return DateTime.fromMillis(val).toFormat('HH:mm, dd/M');
          },
          rotate: -32,
          style: {
            colors: [''],
          },

        },
        axisTicks: {
          // show: props.datasets[0].data.length < 275 ? true : false,
          borderType: 'solid',
          color: '#000',
          height: 7,
        },
        
      },
      yaxis: axisConfigs.map(axis => {
        return {
          showAlways: true,
          showForNullSeries: true,
          min: axis.min,
          max: axis.max,
          tickAmount: axis.tickAmount,
          decimalsInFloat: axis.decimalsInFloat,
          labels: {
            show: !compactAxes,
            offsetX: 0,
            style: {
              colors: axis.color,
            },
            formatter: axis.formatter,
          },
          title: {
            text: compactAxes || isXsScreen ? undefined : axis.label,
            style: {
              color: axis.color,
            },
          },
        }
      }),
      grid: {
        ...(compactAxes
          ? {
              padding: {
                top: 6,
                right: 18,
                bottom: 4,
                left: 18,
              },
            }
          : {}),
        yaxis: {
          lines: {
            show: props.detailed ?? true,
          },
        },
      },
    };
  };

  const getOptions = () => {
    const opt = options();
    if (props.fill && props.fill === true) {
      opt.fill = {
        type: 'gradient',
        gradient: {
          colorStops: [
            {
              offset: 0,
              color: props.colors
                ? props.colors[1]
                : DEFAULT_GRADIENT_COLORS[1],
              opacity: 1,
            },
            {
              offset: 100,
              color: props.colors
                ? props.colors[0]
                : DEFAULT_GRADIENT_COLORS[0],
              opacity: 1,
            },
          ],
        },
      };
    }

    return opt;
  };

  // const getDatasetName = measurmentName => {
  //   return MeasurmentDisplayString[measurmentName];
  // };

  return (
    <div
      ref={rootRef}
      className={`solin-line-chart${compactAxes ? ' solin-line-chart-compact' : ''}`}
      style={{ height: props.height ?? '100%', width: props.width ?? '100%' }}>
      <style>
        {`
          .solin-line-chart {
            box-sizing: border-box;
          }

          .solin-line-chart .apexcharts-legend-series,
          .solin-line-chart .apexcharts-legend-marker,
          .solin-line-chart .apexcharts-legend-text {
            cursor: pointer;
          }

          .solin-line-chart.solin-line-chart-compact {
            display: flex;
            overflow: hidden;
          }

          .solin-line-chart.solin-line-chart-compact .solin-compact-axis-strip {
            box-sizing: border-box;
            display: grid;
            flex: 0 0 auto;
            height: 100%;
            padding: 8px 2px 28px 2px;
            border-right: 1px solid rgba(0, 0, 0, 0.12);
            background: #fff;
          }

          .solin-line-chart.solin-line-chart-compact .solin-compact-axis {
            min-width: 0;
            height: 100%;
            position: relative;
          }

          .solin-line-chart.solin-line-chart-compact .solin-compact-axis-label {
            box-sizing: border-box;
            position: absolute;
            left: 50%;
            bottom: 8px;
            max-width: 100%;
            overflow: hidden;
            color: inherit;
            font-size: 8px;
            font-weight: 700;
            line-height: 1;
            text-align: center;
            text-overflow: ellipsis;
            white-space: nowrap;
            writing-mode: vertical-rl;
            transform: translateX(-50%) rotate(180deg);
          }

          .solin-line-chart.solin-line-chart-compact .solin-compact-axis-ticks {
            position: absolute;
            left: 0;
            right: 0;
            top: var(--compact-plot-top, 0px);
            height: var(--compact-plot-height, 100%);
            width: 100%;
          }

          .solin-line-chart.solin-line-chart-compact .solin-compact-axis-value {
            position: absolute;
            left: 0;
            right: 0;
            max-width: 100%;
            overflow: hidden;
            color: inherit;
            font-size: 9px;
            font-weight: 600;
            line-height: 1;
            text-align: center;
            text-overflow: clip;
            transform: translateY(-50%);
            white-space: nowrap;
          }

          .solin-line-chart.solin-line-chart-compact .solin-compact-chart-body {
            flex: 1 1 auto;
            min-width: 0;
            height: 100%;
            overflow: hidden;
          }

          .solin-line-chart .solin-chart-body {
            height: 100%;
            width: 100%;
          }

          .solin-line-chart .apexcharts-toolbar {
            max-width: none;
            gap: 4px;
          }

          .solin-line-chart .apexcharts-toolbar-custom-icon.solin-chart-range-button {
            order: -1;
            width: auto;
            height: 22px;
            line-height: 22px;
            transform: none;
          }

          .solin-line-chart .apexcharts-toolbar-custom-icon.solin-chart-expand-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .solin-line-chart.solin-line-chart-compact .apexcharts-yaxis-title {
            display: none;
          }

          .solin-line-chart.solin-line-chart-compact .apexcharts-yaxis-label {
            display: none;
          }

          @media (max-width: 599.95px) {
            .solin-line-chart .apexcharts-toolbar .apexcharts-menu-icon,
            .solin-line-chart .apexcharts-toolbar .apexcharts-selection-icon,
            .solin-line-chart .apexcharts-toolbar .apexcharts-zoom-icon,
            .solin-line-chart .apexcharts-toolbar .apexcharts-pan-icon,
            .solin-line-chart .apexcharts-toolbar .apexcharts-reset-icon {
              display: none;
            }
          }
        `}
      </style>
      {compactAxes && (
        <div
          className="solin-compact-axis-strip"
          style={{
            width: `clamp(88px, ${Math.max(axisConfigs.length, 1) * 28}px, 42%)`,
            gridTemplateColumns: `repeat(${Math.max(axisConfigs.length, 1)}, minmax(0, 1fr))`,
          }}>
          {axisConfigs.map(axis => (
            <div className="solin-compact-axis" style={{ color: axis.color }} title={axis.label} key={axis.label}>
              {!isXsScreen && <span className="solin-compact-axis-label">{axis.label}</span>}
              <div
                className="solin-compact-axis-ticks"
                style={{
                  '--compact-plot-top': `${compactPlotBounds.top}px`,
                  '--compact-plot-height': compactPlotBounds.height ? `${compactPlotBounds.height}px` : '100%',
                }}>
                {getCompactAxisTicks(axis).map((tick, index) => (
                  <span
                    className="solin-compact-axis-value"
                    style={{ top: getCompactTickTop(axis, tick) }}
                    key={`${axis.label}-${index}`}>
                    {axis.formatter(tick)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div ref={chartBodyRef} className={`solin-chart-body${compactAxes ? ' solin-compact-chart-body' : ''}`}>
        <Chart
          id={deviceId}
          options={getOptions()}
          series={series}
          // type={props.fill ? 'area' : 'line'}
          type="area"
          height={props.height ?? '100%'}
          width={props.width ?? '100%'}
          ref={chartRef}
        />
      </div>
    </div>
  );
};
